import { Card } from 'app/components/ui/Card'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { ChatMessages } from './ChatMessages'
import { ChatToolbar } from './ChatToolbar'
import { useDropzone } from 'react-dropzone'
import { validateFileSize } from 'app/utils/file'
import { themeEstra } from 'app/styles/themeEstra'

export type ChatMessageType = 'text' | 'file'

export type ChatMessage = {
  id: string
  data: MessageData
  sendMessage?: () => Promise<MessageData> // Promise that will be called on message send
}

export type MessageData = {
  username: string
  isYou: boolean
  message?: string
  attachment_url?: string
  attachment_name?: string
  type: ChatMessageType
  display_sent_at: string // Formatted date string, this will be displayed
  sent_at: DateString
}

export type ChatMessageSend = {
  message?: string
  file?: File
  type: ChatMessageType
  sent_at: DateString
}

interface Props {
  messages?: ChatMessage[]
  onSendMessage: (message: ChatMessageSend) => void
  maxHeight?: string
  className?: string
}

export const Chat: React.FC<Props> = ({
  className,
  messages,
  maxHeight,
  onSendMessage,
}) => {
  const [showFileInput, setShowFileInput] = useState(false)
  const [fileToUpload, setFileToUpload] = useState<File | null>(null)
  const [fileValidationError, setFileValidationError] = useState<string | null>(
    null
  )

  const onFileSelected = (file: File | null) => {
    setFileToUpload(null)
    setFileValidationError(null)

    if (!file) return

    const limitInMb = 10

    const { isValid: isFileSizeValid, error: fileSizeError } = validateFileSize(
      file.size,
      limitInMb
    )

    setShowFileInput(true)

    if (!isFileSizeValid && fileSizeError) {
      setFileValidationError(fileSizeError)
      return
    }

    setFileToUpload(file)
  }

  const toggleShowFileInput = () => {
    setShowFileInput(!showFileInput)
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return
    onFileSelected(acceptedFiles[0])
  }, [])

  const {
    getRootProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    maxFiles: 1,
    onDrop,
  })

  return (
    <DropContainer {...getRootProps()}>
      <DropInnerWrapper
        isDragActive={isDragActive}
        isDragAccept={isDragAccept}
        isDragReject={isDragReject}
      >
        <Card className={className} border padding='0rem'>
          <ChatWrapper>
            <StyledChatMessages
              maxHeight={maxHeight}
              messages={messages}
            ></StyledChatMessages>
            <ChatToolbar
              fileValidationError={fileValidationError}
              fileToUpload={fileToUpload}
              showFileInput={showFileInput}
              onFileSelected={onFileSelected}
              toggleShowFileInput={toggleShowFileInput}
              onSendMessage={onSendMessage}
            ></ChatToolbar>
          </ChatWrapper>
        </Card>
      </DropInnerWrapper>
    </DropContainer>
  )
}
interface DropInnerWrapperProps {
  isDragActive: boolean
  isDragAccept: boolean
  isDragReject: boolean
}

interface StyledChatMessagesProps {
  maxHeight?: string
}

const DropContainer = styled.div`
  outline: none;
`

const DropInnerWrapper = styled.div<DropInnerWrapperProps>`
  opacity: ${({ isDragActive }) => (!isDragActive ? '1' : '0.2')};
`

const ChatWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledChatMessages = styled(ChatMessages)<StyledChatMessagesProps>`
  flex: 1;
  overflow: auto;
  max-height: ${({ maxHeight }) => maxHeight ?? null};
  min-height: 2rem;

  @media (max-width: ${themeEstra.bp.desktop}) {
    padding: 1rem 1rem;
  }
`
